import _ from 'lodash'
import { useEffect, useState } from 'react'
import classnames from 'classnames'
import { CheckOutlined } from '@ant-design/icons'
import { Card } from 'antd'
import { shipmentLines_shipment_shipmentLines_items_holes } from '@Orders/OrderDetailsPage/Queries/__generated__/shipmentLines'

import Box from '~/core/Box'
import Text from '~/core/Text'
import icons from '~/apps/consumer/icons'
import computeHoleGroups from '~/lib/computeHoleGroups'
import getColor from '~/lib/getColor'
import {
    TapsSidebarType,
    GroupType,
} from '~/apps/consumer/components/PartViewerModal/types'
import dfmstyles from '~/apps/consumer/components/TapsSidebarDfmUpdate/styles.css'
import styles from '../../styles.css'

import {
    hasTap,
    renderGroupTapOptions,
    renderHolesHeader,
    TapOption,
    TapTooDeep,
} from './HoleGroup'

export default function TapsSideBar(props: TapsSidebarType) {
    const [groupedHolesMenus, setGroupedHolesMenus] = useState<{
        [key: string]: boolean
    }>({})

    const onMouseEnterHoleGroup = (groupKey: string) => {
        return () => {
            return props.onHoverHoleGroup(groupKey)
        }
    }

    useEffect(() => {
        if (
            props.clickedFeatureId &&
            props.clickedFeatureId !== props.clickedFeatureId
        ) {
            const hole = props.holes.find(({ id }) => {
                return id === props.clickedFeatureId
            })

            if (hole) {
                const groupedHolesMenusState = {
                    ...groupedHolesMenus,
                    [hole.groupKey]: true,
                }

                _.defer(() => setGroupedHolesMenus(groupedHolesMenusState))
            }
        }
    }, [props.clickedFeatureId, groupedHolesMenus, props.holes])

    const onOpenHoleSubMenu = (groupKey: string) => {
        return () => {
            const groupedHolesMenusState = {
                ...groupedHolesMenus,
                [groupKey]: !groupedHolesMenus[groupKey],
            }

            setGroupedHolesMenus(groupedHolesMenusState)
        }
    }

    const onMouseLeaveHoleGroup = () => {
        return props.onHoverHoleGroup(null)
    }

    const renderHoleTapOptions = (
        hole: shipmentLines_shipment_shipmentLines_items_holes,
    ) => {
        const { tapAnnotation } = hole
        const tapMaxDepth = _.get(tapAnnotation, 'tapOption.maxDepth')
        const isHoleDeeperThanTap = tapMaxDepth && _.lt(tapMaxDepth, hole.depth)

        return (
            <Box direction="col">
                <Box className={classnames(styles.tapMenu)}>
                    {TapOption(tapAnnotation?.tapOption)}
                </Box>
                {isHoleDeeperThanTap && TapTooDeep()}
            </Box>
        )
    }

    const onMouseEnterHole = (featureId: string) => {
        return () => {
            return props.onHoverHole(featureId)
        }
    }

    const onMouseLeaveHole = () => {
        return props.onHoverHole(null)
    }

    const renderHole = (
        hole: shipmentLines_shipment_shipmentLines_items_holes,
    ) => {
        const isConfigured = Boolean(hole.tapAnnotation)

        const { hoveredFeatureId } = props
        const selected = hoveredFeatureId === hole.id

        return (
            <li
                key={hole.id}
                className={classnames(
                    styles.hslHole,
                    selected && styles.hslHoleSelected,
                )}
                onMouseEnter={onMouseEnterHole(hole.id)}
                onMouseLeave={onMouseLeaveHole}
            >
                <Box className={styles.hslHoleWrap}>
                    <Box>
                        <img
                            src={
                                isConfigured
                                    ? icons.configuredThreadIcon
                                    : icons.threadIcon
                            }
                            width="15px"
                            height="15px"
                            style={{ margin: '2px 10px 0 0' }}
                        />
                    </Box>
                    <Box className={styles.hslSelectWrap}>
                        {renderHoleTapOptions(hole)}
                    </Box>
                </Box>
            </li>
        )
    }

    const renderHolesList = (group: GroupType) => {
        const menuOpen = groupedHolesMenus[group.groupKey]

        return (
            <Box>
                <ul
                    className={dfmstyles.holesSubList}
                    style={{ display: menuOpen ? 'block' : 'none' }}
                >
                    {group.holes.map(renderHole)}
                </ul>
            </Box>
        )
    }

    const renderHoleGroup = (group: GroupType) => {
        const firstHoleInGroup = group.holes[0]

        if (firstHoleInGroup.tapOptions.length === 0) {
            return null
        }
        const groupHasConfiguredTaps = group.holes.some(hasTap)
        const configuredHoles = group.holes.filter(hasTap)
        let roundedDepth = firstHoleInGroup.depth
        const roundExp = 10

        roundedDepth =
            roundedDepth && Math.round(roundedDepth * roundExp) / roundExp

        return (
            <Card
                activeTabKey={group.groupKey}
                className={classnames(dfmstyles.holeGroup, dfmstyles.cell)}
                onMouseEnter={onMouseEnterHoleGroup(group.groupKey)}
                onMouseLeave={onMouseLeaveHoleGroup}
                title={
                    <div
                        className={classnames(dfmstyles.groupTag, {
                            [dfmstyles.groupHasConfiguredTaps]:
                                groupHasConfiguredTaps,
                        })}
                    >
                        {group.groupTag}
                    </div>
                }
                extra={
                    <Box
                        className={classnames(dfmstyles.threadTag, {
                            [dfmstyles.groupHasConfiguredTaps]:
                                groupHasConfiguredTaps,
                        })}
                    >
                        <Text fontSize="14px" style={{ fontWeight: '400' }}>
                            {configuredHoles.length}/{group?.holes.length}{' '}
                            threads specified
                        </Text>
                        {groupHasConfiguredTaps && (
                            <CheckOutlined style={{ marginLeft: '9px' }} />
                        )}
                    </Box>
                }
                headStyle={{ padding: '0 16px' }}
                bodyStyle={{ padding: '16px 16px 10px' }}
                style={{
                    backgroundColor: getColor('grey2'),
                    border: `1px solid ${getColor('blackAlpha15')}`,
                    color: getColor('blackAlpha65'),
                    margin: '8px 0px',
                }}
            >
                <Box width="100%">
                    <Box direction="col" width="101px">
                        <Text className={dfmstyles.headerText}>Type</Text>
                        <Box className={dfmstyles.cellText}>
                            {firstHoleInGroup.type.replace('_hole', '')}
                        </Box>
                    </Box>
                    <Box direction="col" width="101px">
                        <Text className={dfmstyles.headerText}>Diameter</Text>
                        <Box className={dfmstyles.cellText}>
                            {Number(firstHoleInGroup.diameter)}
                            mm
                        </Box>
                    </Box>
                    <Box direction="col" width="101px">
                        <Text className={dfmstyles.headerText}>Hole depth</Text>
                        <Box className={dfmstyles.cellText}>
                            {roundedDepth}
                            mm
                        </Box>
                    </Box>
                </Box>
                <Box padding="10px 0 8px">{renderGroupTapOptions(group)}</Box>
                <Box direction="col" className={dfmstyles.holesListNew}>
                    {renderHolesHeader(
                        group,
                        groupedHolesMenus,
                        onOpenHoleSubMenu,
                    )}
                    {renderHolesList(group)}
                </Box>
            </Card>
        )
    }

    const { holes } = props

    return (
        <Box className={dfmstyles.list}>
            {computeHoleGroups(holes).map(renderHoleGroup)}
        </Box>
    )
}
